"use client"

import { useMemo, useState } from "react"
import { Box, Flex, Table, Thead, Tbody, Tr, Th, Td, Text, Input, InputGroup, InputLeftElement } from "@chakra-ui/react"
import { SearchIcon } from "@chakra-ui/icons"

import { Panel } from "../../../components/shared/Section"
import { TableSkeleton } from "../../../components/shared/LoadingBlock"
import { neutral, signal, type, numeric } from "../../../theme/tokens"
import { fmtR, fmtPct, pctTone } from "../format"

const th = { fontSize: type.size.micro, letterSpacing: type.tracking.label, color: neutral[500], bg: neutral[25], borderColor: neutral[200], py: 2, position: "sticky", top: 0, zIndex: 1 }
const numTd = { textAlign: "right", sx: numeric, fontSize: type.size.data, py: 1.5, borderColor: neutral[100], whiteSpace: "nowrap" }

const Pct = ({ v }) => (
  <Text as="span" color={pctTone(v, signal, neutral)} fontWeight={v == null ? type.weight.regular : type.weight.semibold}>
    {fmtPct(v)}
  </Text>
)

/**
 * The FILE sheet on screen: every store assigned to the selected rep (blank where nothing
 * sold in a month — the workbook leaves those cells empty too), total row on top as in
 * the workbook, and a filter box because a rep can carry 50 stores.
 */
const RepTracker = ({ data, isLoading }) => {
  const [filter, setFilter] = useState("")
  // Combined view (rep = ALL): every store of every rep, with a Rep column; the filter also
  // matches the rep name.
  const combined = data?.rep === "ALL"
  const stores = useMemo(() => {
    const rows = data?.stores || []
    const q = filter.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((s) => s.site_code.toLowerCase().includes(q) || (s.site_description || "").toLowerCase().includes(q) || (s.rep_name || "").toLowerCase().includes(q))
  }, [data, filter])

  if (isLoading || !data) return <Panel padded={false}><TableSkeleton rows={12} minH="360px" /></Panel>
  const L = data.period?.labels || {}
  const t = data.total || {}

  return (
    <Panel padded={false}>
      <Flex align="center" gap={3} px={3} py={2} borderBottom="1px solid" borderColor={neutral[200]}>
        <Text fontSize={type.size.small} color={neutral[600]} fontWeight={type.weight.semibold} whiteSpace="nowrap">
          {combined ? "Combined · all reps" : data.rep}
        </Text>
        <Text fontSize={type.size.caption} color={neutral[400]} whiteSpace="nowrap">
          {t.storesWithSales ?? 0} of {t.stores ?? 0} stores with sales
        </Text>
        <Box flex="1" />
        <InputGroup size="sm" maxW="260px">
          <InputLeftElement pointerEvents="none"><SearchIcon boxSize="12px" color={neutral[400]} /></InputLeftElement>
          <Input placeholder={combined ? "Find store or rep" : "Find store"} value={filter} onChange={(e) => setFilter(e.target.value)} bg={neutral[0]} />
        </InputGroup>
      </Flex>
      <Box maxH="560px" overflowY="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th {...th}>Store code</Th>
              <Th {...th}>Store name</Th>
              {combined && <Th {...th}>Rep</Th>}
              <Th {...th} textAlign="right">{L.curLY}</Th>
              <Th {...th} textAlign="right">{L.cur} <Text as="span" color={neutral[400]} fontWeight={type.weight.regular}>MTD</Text></Th>
              <Th {...th} textAlign="right">YoY %</Th>
              <Th {...th} textAlign="right">{L.prevLY}</Th>
              <Th {...th} textAlign="right">{L.prev}</Th>
              <Th {...th} textAlign="right">YoY %</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr bg={neutral[50]} fontWeight={type.weight.semibold}>
              <Td py={1.5} borderColor={neutral[200]} fontSize={type.size.data} color={neutral[700]} colSpan={combined ? 3 : 2}>total</Td>
              <Td {...numTd} borderColor={neutral[200]}>{fmtR(t.cur_ly)}</Td>
              <Td {...numTd} borderColor={neutral[200]}>{fmtR(t.cur_ty)}</Td>
              <Td {...numTd} borderColor={neutral[200]}><Pct v={t.cur_yoy} /></Td>
              <Td {...numTd} borderColor={neutral[200]}>{fmtR(t.prev_ly)}</Td>
              <Td {...numTd} borderColor={neutral[200]}>{fmtR(t.prev_ty)}</Td>
              <Td {...numTd} borderColor={neutral[200]}><Pct v={t.prev_yoy} /></Td>
            </Tr>
            {stores.map((s) => (
              <Tr key={s.site_code} _hover={{ bg: neutral[100] }} color={s.hasSales ? neutral[800] : neutral[400]}>
                <Td py={1.5} borderColor={neutral[100]} fontSize={type.size.data} sx={numeric} whiteSpace="nowrap">{s.site_code}</Td>
                <Td py={1.5} borderColor={neutral[100]} fontSize={type.size.data} whiteSpace="nowrap" maxW="340px" overflow="hidden" textOverflow="ellipsis">{s.site_description}</Td>
                {combined && (
                  <Td py={1.5} borderColor={neutral[100]} fontSize={type.size.data} whiteSpace="nowrap" color={s.rep_name ? neutral[600] : neutral[400]} fontStyle={s.rep_name ? "normal" : "italic"}>
                    {s.rep_name || "Unassigned"}
                  </Td>
                )}
                <Td {...numTd}>{fmtR(s.cur_ly)}</Td>
                <Td {...numTd}>{fmtR(s.cur_ty)}</Td>
                <Td {...numTd}><Pct v={s.cur_yoy} /></Td>
                <Td {...numTd}>{fmtR(s.prev_ly)}</Td>
                <Td {...numTd}>{fmtR(s.prev_ty)}</Td>
                <Td {...numTd}><Pct v={s.prev_yoy} /></Td>
              </Tr>
            ))}
            {stores.length === 0 && (
              <Tr>
                <Td colSpan={combined ? 9 : 8} py={6} textAlign="center" color={neutral[500]} fontSize={type.size.small} borderColor={neutral[100]}>
                  {filter ? `No store matches “${filter}”.` : "No stores assigned to this rep."}
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Panel>
  )
}

export default RepTracker
